import React, { useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import CrossBlack from "../../../assets/images/cross_black.svg";
import CopyShareIcon from "../../../assets/images/copy-share.svg";
import DownloadShareIcon from "../../../assets/images/download-share.svg";
import FacebookShareIcon from "../../../assets/images/facebook-share.svg";
import XShareIcon from "../../../assets/images/x-share.svg";
import MessengerShareIcon from "../../../assets/images/messenger-share.png";
import LinkedinShareIcon from "../../../assets/images/linkedin-share.svg";
import WhatsappShareIcon from "../../../assets/images/whatsapp-share.svg";
import { verseArabicForDisplay, getVerseArabicText, arabicFontFamilyCss } from "../../../assets/js/verseScriptText";

export default function ShareSocialMediaModalUiV2(props) {
  const [shareFormat, setShareFormat] = useState("text"); // 'text' | 'image'
  const [showCopiedState, setShowCopiedState] = useState(false);
  const [imageDownloadState, setImageDownloadState] = useState("idle"); // 'idle' | 'loading' | 'success'
  const [imageSocialShareState, setImageSocialShareState] = useState("idle"); // 'idle' | 'loading'
  const copyResetTimeoutRef = useRef(null);
  const downloadResetTimeoutRef = useRef(null);
  const previewCardRef = useRef(null);
  const title = props?.t?.("share-verse") ?? "Share Verse";
  const rawQuote = (props.quote || "").trim();
  const quoteBlocks = rawQuote
    ? rawQuote.split(/\n\s*\n/).map((part) => part.trim()).filter(Boolean)
    : [];
  const fallbackReference =
    props?.singleVerse?.chapterNo && props?.singleVerse?.verseNo
      ? `(${props.singleVerse.chapterNo}:${props.singleVerse.verseNo})`
      : "";
  const previewSurahRef = quoteBlocks[0] || fallbackReference;
  const contentBlocks = quoteBlocks.slice(1);
  const arabicRegex = /[\u0600-\u06FF]/;
  let previewArabic = "";
  const translationBlocks = [];
  contentBlocks.forEach((block) => {
    if (!previewArabic && arabicRegex.test(block)) {
      previewArabic = block;
      return;
    }
    translationBlocks.push(block);
  });
  if (!previewArabic && getVerseArabicText(props.scriptStyle, props.singleVerse)) {
    previewArabic = verseArabicForDisplay(props.scriptStyle, props.singleVerse);
  }
  const translationEntries = translationBlocks.map((block, index) => {
    const lines = block
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
    if (lines.length > 1) {
      return {
        id: `${index}-${lines[0]}`,
        heading: lines[0],
        body: lines.slice(1).join(" "),
      };
    }
    return {
      id: `${index}-${lines[0] || "translation"}`,
      heading: `Translation ${index + 1}`,
      body: lines[0] || "",
    };
  });
  const previewTranslation = translationEntries
    .map((entry) => `${entry.heading}\n${entry.body}`.trim())
    .filter(Boolean)
    .join("\n\n");
  const previewText = [previewSurahRef, previewArabic, previewTranslation]
    .filter(Boolean)
    .join("\n\n");
  const socialIconButtonStyle = {
    backgroundColor: "transparent",
    border: "none",
    padding: 0,
    margin: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  };

  const fallbackCopyText = (text) => {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  };

  const handlePreviewCopy = async () => {
    if (shareFormat === "image") return;

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(previewText);
      } else {
        fallbackCopyText(previewText);
      }
      setShowCopiedState(true);
      if (copyResetTimeoutRef.current) {
        clearTimeout(copyResetTimeoutRef.current);
      }
      copyResetTimeoutRef.current = setTimeout(() => {
        setShowCopiedState(false);
      }, 2500);
    } catch (error) {
      fallbackCopyText(previewText);
      setShowCopiedState(true);
      if (copyResetTimeoutRef.current) {
        clearTimeout(copyResetTimeoutRef.current);
      }
      copyResetTimeoutRef.current = setTimeout(() => {
        setShowCopiedState(false);
      }, 2500);
    }
  };

  const handlePreviewDownload = async () => {
    if (!previewCardRef.current || imageDownloadState === "loading") return;

    setImageDownloadState("loading");

    try {
      const capturedCanvas = await html2canvas(previewCardRef.current, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
        ignoreElements: (element) =>
          element.classList?.contains("share-modal-v2-preview-copy") ||
          element.classList?.contains("share-modal-v2-copied-msg"),
      });

      const targetWidth = 1024;
      const targetHeight = Math.round(
        (capturedCanvas.height / capturedCanvas.width) * targetWidth
      );
      const outputCanvas = document.createElement("canvas");
      outputCanvas.width = targetWidth;
      outputCanvas.height = targetHeight;

      const outputContext = outputCanvas.getContext("2d");
      if (!outputContext) {
        setImageDownloadState("idle");
        return;
      }

      outputContext.clearRect(0, 0, targetWidth, targetHeight);
      outputContext.drawImage(capturedCanvas, 0, 0, targetWidth, targetHeight);

      const imageDataUrl = outputCanvas.toDataURL("image/png");
      const downloadLink = document.createElement("a");
      downloadLink.href = imageDataUrl;
      downloadLink.download = "verse-share.png";
      downloadLink.click();

      setImageDownloadState("success");
      if (downloadResetTimeoutRef.current) {
        clearTimeout(downloadResetTimeoutRef.current);
      }
      downloadResetTimeoutRef.current = setTimeout(() => {
        setImageDownloadState("idle");
      }, 2500);
    } catch (error) {
      setImageDownloadState("idle");
    }
  };

  const createPreviewImageBlob = async () => {
    if (!previewCardRef.current) return null;

    const capturedCanvas = await html2canvas(previewCardRef.current, {
      backgroundColor: null,
      scale: 2,
      useCORS: true,
      ignoreElements: (element) =>
        element.classList?.contains("share-modal-v2-preview-copy") ||
        element.classList?.contains("share-modal-v2-copied-msg"),
    });

    const targetWidth = 1024;
    const targetHeight = Math.round(
      (capturedCanvas.height / capturedCanvas.width) * targetWidth
    );
    const outputCanvas = document.createElement("canvas");
    outputCanvas.width = targetWidth;
    outputCanvas.height = targetHeight;

    const outputContext = outputCanvas.getContext("2d");
    if (!outputContext) return null;

    outputContext.clearRect(0, 0, targetWidth, targetHeight);
    outputContext.drawImage(capturedCanvas, 0, 0, targetWidth, targetHeight);

    return await new Promise((resolve) => {
      outputCanvas.toBlob((blob) => resolve(blob), "image/png");
    });
  };

  const handleImageSocialShare = async () => {
    if (imageSocialShareState === "loading") return;

    setImageSocialShareState("loading");
    try {
      const imageBlob = await createPreviewImageBlob();
      if (!imageBlob) {
        setImageSocialShareState("idle");
        return;
      }

      const file = new File([imageBlob], "verse-share.png", { type: "image/png" });
      const sharePayload = {
        files: [file],
        title,
        text: props.quote || previewText,
      };

      const canUseFileShare =
        navigator?.share &&
        navigator?.canShare &&
        navigator.canShare({ files: [file] });

      if (canUseFileShare) {
        await navigator.share(sharePayload);
      } else {
        await handlePreviewDownload();
      }
    } catch (error) {
      await handlePreviewDownload();
    } finally {
      setImageSocialShareState("idle");
    }
  };

  const handleTextSocialShare = async (platform) => {
    const shareText = previewText;
    const encodedText = encodeURIComponent(shareText);
    const canUseNativeTextShare = typeof navigator?.share === "function";

    try {
      switch (platform) {
        case "facebook":
          if (canUseNativeTextShare) {
            await navigator.share({ title, text: shareText });
          } else {
            await handlePreviewCopy();
            window.open("https://www.facebook.com/", "_blank", "noopener,noreferrer");
          }
          break;
        case "twitter":
          await handlePreviewCopy();
          window.open(
            `https://twitter.com/intent/tweet?text=${encodedText}`,
            "_blank",
            "noopener,noreferrer"
          );
          break;
        case "messenger":
          if (canUseNativeTextShare) {
            await navigator.share({ title, text: shareText });
          } else {
            await handlePreviewCopy();
            window.open("https://www.messenger.com/", "_blank", "noopener,noreferrer");
          }
          break;
        case "linkedin":
          window.open(
            `https://www.linkedin.com/feed/?shareActive=true&text=${encodedText}`,
            "_blank",
            "noopener,noreferrer"
          );
          break;
        case "whatsapp":
          window.open(
            `https://wa.me/?text=${encodedText}`,
            "_blank",
            "noopener,noreferrer"
          );
          break;
        default:
          await handlePreviewCopy();
          break;
      }
    } catch (error) {
      await handlePreviewCopy();
    }
  };

  const handlePreviewAction = async () => {
    if (shareFormat === "image") {
      await handlePreviewDownload();
      return;
    }
    await handlePreviewCopy();
  };

  useEffect(() => {
    if (shareFormat === "image") {
      setShowCopiedState(false);
      return;
    }
    setImageDownloadState("idle");
    setImageSocialShareState("idle");
  }, [shareFormat]);

  useEffect(() => {
    return () => {
      if (copyResetTimeoutRef.current) {
        clearTimeout(copyResetTimeoutRef.current);
      }
      if (downloadResetTimeoutRef.current) {
        clearTimeout(downloadResetTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      className="modal show share-modal-v2-wrapper"
      tabIndex="-1"
      onClick={(e) =>
        e.target.classList.contains("modal") && props.closeSocialMediaModal()
      }
      id="shareVerseModalV2"
      role="dialog"
      aria-labelledby="shareVerseModalV2Label"
      aria-modal="true"
    >
      <div
        className="dark__overlay"
        onClick={(e) =>
          e.target.classList.contains("dark__overlay") &&
          props.closeSocialMediaModal()
        }
      />

      <div className="modal-dialog modal-dialog-centered share-modal-v2-dialog">
        <div className="modal-content share-modal-v2-content">
          <div className="share-modal-v2-header">
            <button
              type="button"
              className="share-modal-v2-close"
              onClick={() => props.closeSocialMediaModal()}
              aria-label={props.t?.("cancel") ?? "Close"}
            >
              <img src={CrossBlack} alt="" />
            </button>

            <h2 id="shareVerseModalV2Label" className="share-modal-v2-title">
              {title}
            </h2>
            <p className="share-modal-v2-subtitle">
              Select your preferred format to share the verse on social media.
            </p>
          </div>

          <div className="share-modal-v2-body">
            <div className="share-modal-v2-format-toggle">
              <button
                type="button"
                className={`share-modal-v2-format-option ${
                  shareFormat === "text" ? "active" : ""
                }`}
                onClick={() => setShareFormat("text")}
              >
                <span className="share-modal-v2-radio">
                  {shareFormat === "text" && (
                    <span className="share-modal-v2-radio-dot" />
                  )}
                </span>
                Share as text
              </button>

              <button
                type="button"
                className={`share-modal-v2-format-option ${
                  shareFormat === "image" ? "active" : ""
                }`}
                onClick={() => setShareFormat("image")}
              >
                <span className="share-modal-v2-radio">
                  {shareFormat === "image" && (
                    <span className="share-modal-v2-radio-dot" />
                  )}
                </span>
                Share as Image
              </button>
            </div>

            <div className="share-modal-v2-preview-card" ref={previewCardRef}>
              <button
                type="button"
                className="share-modal-v2-preview-copy"
                title={
                  shareFormat === "image"
                    ? imageDownloadState === "loading"
                      ? "Downloading"
                      : imageDownloadState === "success"
                      ? "Downloaded"
                      : "Download"
                    : showCopiedState
                    ? "Copied"
                    : "Copy"
                }
                onClick={handlePreviewAction}
                disabled={shareFormat === "image" && imageDownloadState === "loading"}
              >
                {shareFormat === "image" ? (
                  imageDownloadState === "loading" ? (
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-label="Downloading"
                    />
                  ) : imageDownloadState === "success" ? (
                    <span
                      aria-label="Downloaded"
                      role="img"
                      className="share-modal-v2-preview-check"
                    >
                      ✓
                    </span>
                  ) : (
                    <img src={DownloadShareIcon} alt="" />
                  )
                ) : showCopiedState ? (
                  <span
                    aria-label="Copied"
                    role="img"
                    className="share-modal-v2-preview-check"
                  >
                    ✓
                  </span>
                ) : (
                  <img src={CopyShareIcon} alt="" />
                )}
              </button>
              {showCopiedState && shareFormat === "text" && (
                <span className="share-modal-v2-copied-msg" aria-live="polite">
                  Copied
                </span>
              )}
              {imageDownloadState === "success" && shareFormat === "image" && (
                <span className="share-modal-v2-copied-msg" aria-live="polite">
                  Downloaded
                </span>
              )}

              <div className="share-modal-v2-preview-top">
                <div className="share-modal-v2-preview-surah">
                  {previewSurahRef}
                </div>
              </div>

              {previewArabic ? (
                <p
                  className="share-modal-v2-preview-arabic"
                  dir="rtl"
                  style={{ fontFamily: arabicFontFamilyCss(props.scriptStyle) }}
                >
                  {previewArabic}
                </p>
              ) : null}
              {translationEntries.length > 0
                ? translationEntries.map((entry) => (
                    <p key={entry.id} className="share-modal-v2-preview-translation">
                      <strong>{entry.heading}</strong>
                      {entry.body ? <><br />{entry.body}</> : null}
                    </p>
                  ))
                : null}
            </div>

            <div className="share-modal-v2-social-bar">
              <div className="share-modal-v2-social-icon-wrap">
                <button
                  type="button"
                  onClick={
                    shareFormat === "image"
                      ? handleImageSocialShare
                      : () => handleTextSocialShare("facebook")
                  }
                  className="react-share__ShareButton"
                  style={socialIconButtonStyle}
                  aria-label={shareFormat === "image" ? "Share image" : "Share text on Facebook"}
                >
                  <img src={FacebookShareIcon} alt="" />
                </button>
              </div>

              <div className="share-modal-v2-social-icon-wrap">
                <button
                  type="button"
                  onClick={
                    shareFormat === "image"
                      ? handleImageSocialShare
                      : () => handleTextSocialShare("twitter")
                  }
                  className="react-share__ShareButton"
                  style={socialIconButtonStyle}
                  aria-label={shareFormat === "image" ? "Share image" : "Share text on X"}
                >
                  <img src={XShareIcon} alt="" />
                </button>
              </div>

              <div className="share-modal-v2-social-icon-wrap">
                <button
                  type="button"
                  onClick={
                    shareFormat === "image"
                      ? handleImageSocialShare
                      : () => handleTextSocialShare("messenger")
                  }
                  className="react-share__ShareButton"
                  style={socialIconButtonStyle}
                  aria-label={shareFormat === "image" ? "Share image" : "Share text on Messenger"}
                >
                  <img src={MessengerShareIcon} alt="" />
                </button>
              </div>

              <div className="share-modal-v2-social-icon-wrap">
                <button
                  type="button"
                  onClick={
                    shareFormat === "image"
                      ? handleImageSocialShare
                      : () => handleTextSocialShare("linkedin")
                  }
                  className="react-share__ShareButton"
                  style={socialIconButtonStyle}
                  aria-label={shareFormat === "image" ? "Share image" : "Share text on LinkedIn"}
                >
                  <img
                    src={LinkedinShareIcon}
                    alt=""
                    className="share-modal-v2-icon-linkedin"
                  />
                </button>
              </div>

              <div className="share-modal-v2-social-icon-wrap">
                <button
                  type="button"
                  onClick={
                    shareFormat === "image"
                      ? handleImageSocialShare
                      : () => handleTextSocialShare("whatsapp")
                  }
                  className="react-share__ShareButton"
                  style={socialIconButtonStyle}
                  aria-label={shareFormat === "image" ? "Share image" : "Share text on WhatsApp"}
                >
                  <img src={WhatsappShareIcon} alt="" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

